import { computed, nextTick } from 'vue'
import { consola } from 'consola'
import { useLogoutHandler } from './useLogoutHandler'
import { useTabsSync, TabSyncEventType } from '@shared/lib'
import apiAuth, { useSessionManager } from '~/entities/auth'
import { AuthLogoutService } from '~/features/auth/lib/AuthLogoutService'

const _logger = consola.withTag('token-refresh-v2')
const MAX_FAILURE_COUNT = 2

enum ErrorType {
  UNAUTHORIZED = 'UNAUTHORIZED',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALID = 'TOKEN_INVALID',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN = 'UNKNOWN',
}

let globalRefreshPromise: Promise<string | null> | null = null
let globalIsRefreshing = false
const pendingRequests: Array<{ resolve: (token: string | null) => void; reject: (error: any) => void }> = []
let backgroundRefreshTimer: NodeJS.Timeout | null = null
const BACKGROUND_CHECK_INTERVAL_MS = 30_000

const determineErrorType = (status: number | undefined, message: string): ErrorType => {
  if (status === 401 || status === 403) return ErrorType.UNAUTHORIZED
  const lowerMessage = message.toLowerCase()
  if (lowerMessage.includes('expired') || lowerMessage.includes('истек')) return ErrorType.TOKEN_EXPIRED
  if (lowerMessage.includes('invalid') || lowerMessage.includes('недействительн')) return ErrorType.TOKEN_INVALID
  if (lowerMessage.includes('cors') || lowerMessage.includes('failed to fetch')) return ErrorType.NETWORK_ERROR
  return ErrorType.UNKNOWN
}

const isNetworkError = (errorType: ErrorType, status: number | undefined): boolean => {
  return errorType === ErrorType.NETWORK_ERROR || !status
}

const resolvePendingRequests = (token: string): void => {
  while (pendingRequests.length > 0) pendingRequests.shift()?.resolve(token)
}

const rejectPendingRequests = (error: any): void => {
  while (pendingRequests.length > 0) pendingRequests.shift()?.reject(error)
}

export const useTokenRefreshV2 = () => {
  const sessionManager = useSessionManager()
  const logoutHandler = useLogoutHandler()
  const tabsSync = useTabsSync()

  if (process.client) {
    tabsSync.on(TabSyncEventType.TOKEN_REFRESHED, (event) => {
      sessionManager.setSession(event.data.accessToken, event.data.sessionId, event.data.refreshToken)
      if (globalIsRefreshing && globalRefreshPromise) {
        resolvePendingRequests(event.data.accessToken)
        globalIsRefreshing = false
        globalRefreshPromise = null
        logoutHandler.setRefreshingToken(false)
        logoutHandler.resetRefreshTokenFailureCount()
      }
    })
    tabsSync.on(TabSyncEventType.TOKEN_REFRESH_STARTED, () => {})
    tabsSync.on(TabSyncEventType.TOKEN_REFRESH_FAILED, () => {})
  }

  const waitForTokenRefresh = (): Promise<string | null> => {
    return new Promise((resolve, reject) => pendingRequests.push({ resolve, reject }))
  }

  const handleRefreshError = (errorType: ErrorType, currentFailureCount: number): void => {
    if (
      errorType === ErrorType.UNAUTHORIZED ||
      errorType === ErrorType.TOKEN_EXPIRED ||
      errorType === ErrorType.TOKEN_INVALID ||
      (errorType === ErrorType.UNKNOWN && currentFailureCount >= MAX_FAILURE_COUNT)
    ) {
      logoutHandler.handleRefreshTokenExpired()
    }
  }

  const refreshToken = (): Promise<string | null> => {
    const currentFailureCount = logoutHandler.getRefreshTokenFailureCount()
    if (AuthLogoutService.isLoggingOut()) return null
    if (globalIsRefreshing && globalRefreshPromise) return globalRefreshPromise
    if (currentFailureCount >= MAX_FAILURE_COUNT) return null

    const refreshTokenValue = sessionManager.authRefreshToken.value
    if (!refreshTokenValue) {
      /* v8 ignore next 3 */
      const storedRefreshToken = process.client ? localStorage.getItem('authRefreshToken') : null
      if (storedRefreshToken) sessionManager.authRefreshToken.value = storedRefreshToken
      else return null
    }

    globalIsRefreshing = true
    logoutHandler.setRefreshingToken(true)
    tabsSync.broadcast(TabSyncEventType.TOKEN_REFRESH_STARTED)

    globalRefreshPromise = (async () => {
      try {
        const { data } = await apiAuth.refresh(sessionManager.authRefreshToken.value!)
        if (data.accessToken && data.sessionId && data.refreshToken) {
          await sessionManager.setSession(data.accessToken, data.sessionId, data.refreshToken)
          await nextTick()
          logoutHandler.resetRefreshTokenFailureCount()
          resolvePendingRequests(data.accessToken)
          tabsSync.broadcast(TabSyncEventType.TOKEN_REFRESHED, {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            sessionId: data.sessionId,
          })
          return data.accessToken
        }
        rejectPendingRequests(new Error('Tokens не получены'))
        return null
      } catch (error: any) {
        const status = error?.status
        const message = error?.data?.message || error?.message || ''
        const errorType = determineErrorType(status, message)
        if (!isNetworkError(errorType, status)) logoutHandler.incrementRefreshTokenFailureCount()
        rejectPendingRequests(error)
        handleRefreshError(errorType, logoutHandler.getRefreshTokenFailureCount())
        tabsSync.broadcast(TabSyncEventType.TOKEN_REFRESH_FAILED, { errorType, status, message })
        return null
      /* v8 ignore next 4 */
      } finally {
        globalIsRefreshing = false
        globalRefreshPromise = null
        logoutHandler.setRefreshingToken(false)
      }
    })()

    return globalRefreshPromise
  }

  const ensureValidToken = async (): Promise<boolean> => {
    if (AuthLogoutService.isLoggingOut()) return false
    if (sessionManager.authAccessToken.value) return true
    if (process.client) {
      const restored = sessionManager.restoreSessionFromStorage()
      if (restored && sessionManager.authAccessToken.value) return true
    }
    if (!sessionManager.authRefreshToken.value) return false
    return !!(await refreshToken())
  }

  const handle403Error = async (): Promise<void> => {
    await logoutHandler.handleRefreshTokenExpired()
  }

  const startBackgroundRefresh = (): void => {
    if (backgroundRefreshTimer || !process.client) return
    backgroundRefreshTimer = setInterval(async () => {
      if (AuthLogoutService.isLoggingOut()) return stopBackgroundRefresh()
      if (!sessionManager.hasActiveSession.value) return
      if (sessionManager.shouldRefreshToken()) await refreshToken()
    }, BACKGROUND_CHECK_INTERVAL_MS)
  }

  const stopBackgroundRefresh = (): void => {
    if (!backgroundRefreshTimer) return
    clearInterval(backgroundRefreshTimer)
    backgroundRefreshTimer = null
  }

  const restartBackgroundRefresh = (): void => {
    stopBackgroundRefresh()
    startBackgroundRefresh()
  }

  return {
    authAccessToken: sessionManager.authAccessToken,
    authSessionId: sessionManager.authSessionId,
    hasActiveSession: sessionManager.hasActiveSession,
    isRefreshing: computed(() => globalIsRefreshing),
    refreshToken,
    ensureValidToken,
    handle403Error,
    waitForTokenRefresh,
    startBackgroundRefresh,
    stopBackgroundRefresh,
    restartBackgroundRefresh,
  }
}
